// libs
import React, { memo, useEffect, useState } from 'react';

// styles
import {
  ActionButton,
  CloseIcon,
  ConfirmContainer,
  ConfirmSubtitle,
  ConfirmTitle,
  Loader,
  RemoveButton,
} from './styles';
import { Overlay } from 'shared/components/Modal/styles';

// context
import { useLoaderContext } from 'shared/components/LoaderProvider/context';
import { useWishlistContext } from 'shared/layouts/Wishlist/API/context';

// icons
import CircleXDuotoneIcon from '@nykaa/ui-components/Icons/circle-x-duotone';

// analytics
import { trackRemoveFromWishlistClicked } from 'layouts/Wishlist/analytics';

// hooks
import useTheme from '@nykaa/ui-components/themes/hooks/useTheme';

// types
import { Theme } from '@nykaa/ui-components/themes/types';

//components
import Portal from 'shared/components/Portal';

//helpers
import { DEVICE_TYPE, getDeviceType } from 'shared/helpers/app';
import { hideOverflowY, resetOverflowY } from 'shared/helpers/dom';

// defs
interface IRemove {
  item: Listing.IProduct;
}

const REMOVE_ICON_SIZE = '32px';

function Remove({ item }: IRemove) {
  const { isRemoving, setIsRemoving, removeProductFromWishlist } = useWishlistContext();
  const { showLoader, hideLoader } = useLoaderContext();
  const [showConfirm, setShowConfirm] = useState(false);
  const { productId } = item;

  const wishlistMetaData = {
    productID: productId,
    discountPercent: item?.discount,
    MRP: item?.price,
    productName: item?.subTitle,
    productSKU: item?.sku,
    discountedPrice: item.discountedPrice,
    productBrandName: item?.title,
    isOutOfStock: Boolean(item.isOutOfStock),
    productTags: item?.tag,
    isProductOffersAvailable: 'NA',
    ...(item?.type ? { productType: item?.type } : {}),
    ...(item?.badge?.title ? { productBadge: item?.badge?.title } : {}),
    categories: item?.primary_categories_json_all,
  };

  const theme: Theme = useTheme();

  const isMobile = getDeviceType() === DEVICE_TYPE.MOBILE;

  const removeProductFromWishlistHelper = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsRemoving(true);
    if (isMobile) {
      showLoader();
    }
    removeProductFromWishlist(productId)
      .then(() => {
        hideLoader();
        setIsRemoving(false);
        trackRemoveFromWishlistClicked(wishlistMetaData);
        setShowConfirm(false);
      })
      .catch(() => {
        hideLoader();
        setIsRemoving(false);
        setShowConfirm(false);
      });
  };
  const onRemoveButtonClick = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    setShowConfirm(true);
  };
  useEffect(() => {
    if (showConfirm) hideOverflowY('body', false);
    else resetOverflowY();

    return () => {
      resetOverflowY();
    };
  });

  const onCloseClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowConfirm(false);
  };

  return (
    <RemoveButton
      onClick={isMobile ? removeProductFromWishlistHelper : onRemoveButtonClick}
      disabled={isRemoving}
      data-at="remove-btn"
    >
      <CircleXDuotoneIcon color={theme.colors.textPrimary} size={REMOVE_ICON_SIZE} />
      {showConfirm && (
        <Portal>
          <Overlay hide={!showConfirm} onClick={onCloseClick}>
            <ConfirmContainer>
              <>
                <CloseIcon onClick={onCloseClick} />

                <ConfirmTitle>Confirmation</ConfirmTitle>
                <ConfirmSubtitle>
                  Are you sure you want to remove this item from the wishlist?
                </ConfirmSubtitle>
                <ActionButton
                  onClick={removeProductFromWishlistHelper}
                  kind="primary"
                  shape="default"
                  fullWidth
                  size="large"
                >
                  {isRemoving ? <Loader /> : 'Continue'}
                </ActionButton>
              </>
            </ConfirmContainer>
          </Overlay>
        </Portal>
      )}
    </RemoveButton>
  );
}

export default memo(Remove);
